import { useCallback, useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"

import {
  get as getPersistValue,
  set as setPersistValue,
  remove as removePersistValue,
} from "utils/persist_store"
import { DEFAULT_VENUE_FILTER, FORCE_VENUE_ID_KEY, Q_VENUE } from "constant"

interface IUseVenueControllerResult {
  viewingVenue: string
  changeViewingVenue: (id: string) => void
  initVenueController: () => void
  resetViewingVenue: () => void
}

type UseVenueController = (initialVenueId?: string) => IUseVenueControllerResult

export const useVenueController: UseVenueController = (initialVenueId) => {
  const [searchParams] = useSearchParams()
  const [viewingVenue, setViewingVenue] = useState<string | null>(
    getPersistValue(Q_VENUE) || initialVenueId || DEFAULT_VENUE_FILTER
  )

  const changeViewingVenue = useCallback(
    (id: string) => {
      setViewingVenue(id)
      setPersistValue(Q_VENUE, id || DEFAULT_VENUE_FILTER)
    },
    [setViewingVenue]
  )

  const initVenueController = useCallback(() => {
    setViewingVenue(DEFAULT_VENUE_FILTER)
    removePersistValue(Q_VENUE)
  }, [setViewingVenue])

  const resetViewingVenue = useCallback(() => {
    const forceVenueId = getPersistValue(FORCE_VENUE_ID_KEY)
    setViewingVenue(forceVenueId || initialVenueId)
    removePersistValue(Q_VENUE)
  }, [initialVenueId])

  useEffect(() => {
    const forceVenueId = getPersistValue(FORCE_VENUE_ID_KEY)
    const qVenue = searchParams.get(Q_VENUE)
    if (forceVenueId || qVenue) changeViewingVenue(forceVenueId || qVenue)
  }, [searchParams, changeViewingVenue])

  // Ensure the initial change updates the viewingVenue state.
  useEffect(() => {
    const forceVenueId = getPersistValue(FORCE_VENUE_ID_KEY)
    const prevVenueId = getPersistValue(Q_VENUE)
    changeViewingVenue(
      forceVenueId || prevVenueId || initialVenueId || DEFAULT_VENUE_FILTER
    )
  }, [initialVenueId, changeViewingVenue])

  return {
    viewingVenue,
    changeViewingVenue,
    initVenueController,
    resetViewingVenue,
  }
}
